import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  marketplaceApi,
  MARKETPLACE_LABELS,
  MarketplaceImportJob
} from '../api/marketplace.api';

const DISMISSED_KEY = 'marketplace_import_dismissed';

function getDismissedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function markDismissed(jobId: string) {
  const ids = getDismissedIds();
  ids.add(jobId);
  sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
}

/** Global bottom toast when a Marketplace import finishes (any page). */
const MarketplaceImportToast: React.FC = () => {
  const { user } = useAuth();
  const [toast, setToast] = useState<{
    severity: 'success' | 'error' | 'warning';
    message: string;
  } | null>(null);

  const poll = useCallback(async () => {
    if (user?.role !== 'SUPER_ADMIN') return;
    try {
      const jobs = await marketplaceApi.getActiveJobs();
      const dismissed = getDismissedIds();

      for (const job of jobs as MarketplaceImportJob[]) {
        if (dismissed.has(job.jobId)) continue;
        if (job.status === 'PROCESSING') continue;

        markDismissed(job.jobId);
        const label = MARKETPLACE_LABELS[job.source] || job.source;

        if (job.status === 'COMPLETED') {
          const msg =
            job.result?.body?.message ||
            `Imported into ${label}: ${job.fileName}`;
          setToast({ severity: 'success', message: msg });
          window.dispatchEvent(
            new CustomEvent('marketplace-import-finished', { detail: { job } })
          );
        } else if (job.status === 'FAILED') {
          const reason =
            job.error ||
            job.result?.body?.error?.message ||
            `Import failed for ${label}: ${job.fileName}`;
          setToast({
            severity: 'error',
            message: `Import failed (${label}): ${reason}`
          });
          window.dispatchEvent(
            new CustomEvent('marketplace-import-finished', { detail: { job } })
          );
        } else if (job.status === 'CANCELLED') {
          setToast({
            severity: 'warning',
            message: `Import cancelled for ${label}: ${job.fileName}`
          });
        }
      }
    } catch {
      // ignore (not on marketplace / network blip)
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') return;
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [user?.role, poll]);

  return (
    <Snackbar
      open={Boolean(toast)}
      autoHideDuration={toast?.severity === 'error' ? 20000 : 7000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={toast?.severity || 'success'}
        variant="filled"
        onClose={() => setToast(null)}
        sx={{
          width: '100%',
          maxWidth: 640,
          whiteSpace: 'pre-line',
          alignItems: 'flex-start',
          boxShadow: 3,
          color: '#fff',
          '& .MuiAlert-icon': { color: '#fff' },
          '& .MuiAlert-message': { color: '#fff' },
          '& .MuiAlert-action .MuiIconButton-root': { color: '#fff' }
        }}
      >
        {toast?.message || ''}
      </Alert>
    </Snackbar>
  );
};

export default MarketplaceImportToast;
export { getDismissedIds, markDismissed, DISMISSED_KEY };
